import React, { FormEvent } from 'react';
import { VisibilityTrackerContainer } from '@noths/polaris-client-components';
import { Price } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { UserConfigurationState } from '@noths/polaris-client-user-configuration';
import { DeliveryByEvent } from '@noths/polaris-dev-ts-types';

import { AnchorToSection } from 'src/components/atoms/AnchorToSection';
import { Quantity } from 'src/components/atoms/Quantity';
import { DeliveryBlockContainer } from 'src/components/molecules/DeliveryBlock';
import { DeliveryEventContainer } from 'src/components/molecules/DeliveryEvent';
import { PriceBreakdownContainer } from 'src/components/molecules/PriceBreakdown';
import { StarRating } from 'src/components/molecules/StarRating/components/StarRating';
import { PersonalisationContainer } from 'src/components/organisms/Personalisation';
import {
  InteractionErrorTrackingEvent,
  PRODUCT_PERSONALISATIONS_VISIBILITY_NAME,
  TrackedEvents,
} from 'src/redux/personalisation';
import { FavouriteProduct } from 'src/redux/selectors';
import { Basket, BasketItem, ProductOption } from 'src/types';
import { isMadeToOrder, isOutOfStock, isSuspended, removeHtmlElementFromString } from 'src/utils';
import { NEW_PRODUCT_LABEL, PRODUCT_REVIEWS_ID } from '../constants';
import { AddToBasketButtonContainer } from '../containers/AddToBasketContainer';
import { KlarnaContainer } from '../containers/KlarnaContainer';
import { DELIVERY_SUMMARY_VISIBILITY_NAME } from '../tracking/events';
import { CartModal } from './CartModal';
import { MadeToOrder } from './MadeToOrder';
import { OutOfStock } from './OutOfStock';
import { Suspended } from './Suspended';
import * as styles from '../styles/ProductInfo.styles';

export const PRICE = 'price';
export const PREVIOUS_PRICE = 'previous-price';

export interface ProductInfoProps {
  basket: Basket | null;
  deliveryByEvent: DeliveryByEvent | null;
  editItem: BasketItem | undefined;
  hideAddToCartPlacement: boolean;
  isCartModalOpen: boolean;
  isPersonalisationOptionsExpanded: boolean;
  outstandingSelections: ProductOption[];
  product: FavouriteProduct;
  richRelevanceSessionId?: string;
  shouldShowDeliveryBlock: boolean;
  shouldShowPersonalisationValidationErrors: boolean;
  trackedEvents: TrackedEvents;
  userConfiguration: UserConfigurationState;
  addToBasket: VoidFunction;
  changeQuantity: (payload: { quantity: number; isValid: boolean }) => void;
  onCartGoToBasket: () => void;
  onCartModalClose: VoidFunction;
  onProductReviewsReadReviews: () => void;
  openCartModal: VoidFunction;
  submitPersonalisationForm: () => void;
  toggleExpanded: VoidFunction;
  trackErrorInteraction: (payload: InteractionErrorTrackingEvent) => void;
}

const handleQtyChange = (
  value: string,
  isValid: boolean,
  changeQuantity: ProductInfoProps['changeQuantity'],
) => {
  changeQuantity({ quantity: Number(value), isValid });
};

const handlePersonalisationFormSubmit = (
  e: FormEvent,
  submitPersonalisationForm: ProductInfoProps['submitPersonalisationForm'],
) => {
  e.preventDefault();

  submitPersonalisationForm();
};

export const ProductInfo = ({
  basket,
  deliveryByEvent,
  editItem,
  hideAddToCartPlacement,
  isCartModalOpen,
  isPersonalisationOptionsExpanded,
  product,
  richRelevanceSessionId,
  shouldShowDeliveryBlock,
  shouldShowPersonalisationValidationErrors,
  userConfiguration,
  changeQuantity,
  onCartModalClose,
  toggleExpanded,
  onCartGoToBasket,
  onProductReviewsReadReviews,
  submitPersonalisationForm,
}: ProductInfoProps) => {
  const isGBPCurrency = userConfiguration.currencyCode === 'GBP';
  const preSalePrice = product.preSalePrices?.find(
    (price) => price.currency === product.price.currency,
  );
  const renderInventoryStatus = () => {
    switch (true) {
      case isMadeToOrder(product.inventoryStatus):
        return <MadeToOrder />;
      case isSuspended(product.availability, product.available):
        return <Suspended />;
      case isOutOfStock(product.inventoryStatus):
        return <OutOfStock backInStockAt={product.backInStockAt} />;
      default:
        return null;
    }
  };

  return (
    <div css={styles.productInfoWrapper}>
      <h1 css={styles.heading}>{product.title}</h1>
      <a
        css={styles.link}
        href={`/${product.partner.shortcode}`}
        aria-label={`by ${product.partner.name}`}
      >
        {product.partner.name}
      </a>
      <div css={styles.prices}>
        <div>
          <Price
            testId={PRICE}
            isSalePrice={Boolean(product.onSale && preSalePrice)}
            css={styles.styledPrice}
            {...product.price}
          />
          {product.onSale && preSalePrice && (
            <Price
              testId={PREVIOUS_PRICE}
              isPreviousPrice={true}
              css={styles.previousPrice}
              {...preSalePrice}
            />
          )}
        </div>
      </div>
      {renderInventoryStatus()}
      {deliveryByEvent?.arriveByEvent && <DeliveryEventContainer />}
      {isGBPCurrency && <KlarnaContainer />}
      <hr css={styles.divider} />
      {product.productRating && (
        <AnchorToSection
          css={styles.productRating}
          elementId={PRODUCT_REVIEWS_ID}
          onClick={onProductReviewsReadReviews}
        >
          <StarRating
            productRating={product.productRating}
            numberOfReviews={product.numberOfReviews}
            accessibleLabel={`Overall rating ${product.productRating} out of 5`}
          />
        </AnchorToSection>
      )}
      {product.isNew && <p css={visuallyHidden}>{NEW_PRODUCT_LABEL}</p>}
      <p
        dangerouslySetInnerHTML={{ __html: removeHtmlElementFromString(product.description, 'p') }}
      />
      <form
        onSubmit={(e) => handlePersonalisationFormSubmit(e, submitPersonalisationForm)}
        noValidate
      >
        {product.purchasable && (
          <VisibilityTrackerContainer sectionName={PRODUCT_PERSONALISATIONS_VISIBILITY_NAME}>
            <div>
              <PersonalisationContainer
                toggleExpanded={toggleExpanded}
                displayValidation={shouldShowPersonalisationValidationErrors}
                isExpanded={isPersonalisationOptionsExpanded}
              />
            </div>
          </VisibilityTrackerContainer>
        )}
        {!isOutOfStock(product.inventoryStatus) && (
          <>
            {Boolean(product.options.length) && <PriceBreakdownContainer />}
            <Quantity
              onChange={(value, valid) => handleQtyChange(value, valid, changeQuantity)}
              defaultValue={editItem?.quantity || product.quantity}
            />
          </>
        )}
        <AddToBasketButtonContainer preventAddToBasketSubmit={true} />
      </form>
      {!isOutOfStock(product.inventoryStatus) && (
        <VisibilityTrackerContainer sectionName={DELIVERY_SUMMARY_VISIBILITY_NAME}>
          <div>
            {shouldShowDeliveryBlock && <DeliveryBlockContainer css={styles.deliveryBlock} />}
          </div>
        </VisibilityTrackerContainer>
      )}
      <CartModal
        isModalOpen={isCartModalOpen}
        basket={basket}
        product={product}
        onModalClose={onCartModalClose}
        onModalApply={onCartGoToBasket}
        hideAddToCartPlacement={hideAddToCartPlacement}
        richRelevanceSessionId={richRelevanceSessionId}
      />
    </div>
  );
};
